/* eslint-disable react/require-default-props, consistent-return */
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import Loading from '../../../components/Loading';
import GoalsConstants from '../../../constants/goals.constants';
import ReviewConstants from '../../../constants/review.constants';
import UserContext from '../../../hooks/context/UserContext';
import { AppLayout } from '../../../layouts';
import ApiService from '../../../services/api.service';
import axios from '../../../services/http.service';
import SocketService from '../../../services/socket.service';
import { checkIsEverySubSkillInProgress } from '../../Head/helpers/reviews.helpers';
import Congratulation from '../components/Congratulation';
import ResizablePanel from './components/ResizablePanel';
import SkillDetailView from './components/SkillDetailView';
import SkillTreeSidebar from './components/SkillTreeSidebar';

export default function HomeV3({ title }) {
  const [progress, setProgress] = useState(0);
  const [isReviewRequested, setIsReviewRequested] = useState(null);
  const [hardSkills, setHardSkills] = useState([]);
  const [softSkills, setSoftSkills] = useState([]);
  const [personalSkills, setPersonalSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLastLevel, setIsLastLevel] = useState(false);
  const [isEverythingInProgress, setIsEverythingInProgress] = useState(false);
  const [congratulate, setCongratulate] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [isLoadingBtn, setLoadingBtn] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const { user, socketInstance, setSocketInstance } = useContext(UserContext);

  const handleOnRequestReviewClick = async () => {
    setLoadingBtn(true);
    const result = await ApiService.developer.requestReview();
    if (result.status) {
      setLoadingBtn(false);
      setIsReviewRequested(true);
    }
  };

  const handleLinkClick = useCallback(
    async (payload) => {
      if (!user) return;
      await axios({
        method: 'POST',
        url: 'users-history/task-link-clicked',
        data: {
          taskId: payload.taskId,
          link: payload.link,
        },
      });
    },
    [user],
  );

  const checkReview = useCallback(
    async (goalProgress) => {
      const data = await ApiService.developer.getRequestedReview();

      if (data) {
        setReviewData(data);
        setIsReviewRequested(true);
      } else {
        const confirmed = await ApiService.developer.getConfirmedReview();

        if (confirmed) {
          const foundNonCongratulated = confirmed.some((v) => !v.isCongratulated);

          if (foundNonCongratulated) {
            await ApiService.developer.setAllConfirmedAsCongratulated();
            setCongratulate(foundNonCongratulated);
          }
        }
      }

      if (goalProgress < 100) return;

      const [me, levels] = await Promise.all([
        ApiService.developer.getMySelf(),
        ApiService.developer.getDepartmentLevels(user.departmentId),
      ]);

      setIsLastLevel(_.last(levels)._id === me.level);
    },
    [user.departmentId],
  );

  // Helper to find the first selectable skill (leaf node with details)
  const findFirstSelectableSkill = useCallback((skills, preferInProgress = false) => {
    // Get all leaf nodes (skills with details but no children, or skills whose children are not in the skills array)
    const leafSkills = skills.filter((skill) => {
      const hasDetails = skill.details?.length > 0;
      const hasChildrenInTree = skill.children?.some((childId) =>
        skills.some((s) => s.itemId === childId),
      );
      return hasDetails && !hasChildrenInTree;
    });

    if (preferInProgress) {
      // Find first in-progress skill
      const inProgressSkill = leafSkills.find(
        (skill) => skill.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
      );
      if (inProgressSkill) return inProgressSkill;
    }

    // Return first leaf skill
    return leafSkills[0] || null;
  }, []);

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        await axios({
          method: 'GET',
          url: 'goals',
        }).then((data) => {
          setProgress(data.progress);
          setHardSkills(data?.hardSkills);
          setSoftSkills(data?.softSkills);
          setPersonalSkills(data?.personalSkills);
          setIsLoading(false);

          const allLoadedSkills = [...data.hardSkills, ...data.softSkills, ...data.personalSkills];

          const isProgress = checkIsEverySubSkillInProgress(allLoadedSkills);
          setIsEverythingInProgress(isProgress);

          // Auto-select first in-progress skill, or first skill if none in progress
          const skillToSelect = findFirstSelectableSkill(allLoadedSkills, true);
          if (skillToSelect) {
            setSelectedSkillId(skillToSelect.itemId);
          }

          return checkReview(data.progress);
        });
      } catch (err) {
        setIsLoading(false);
        console.error(err);
      }
    }

    fetchData();
  }, [checkReview, findFirstSelectableSkill]);

  useEffect(() => {
    setSocketInstance(SocketService.getSocketInstance('goals'));

    return () => {
      socketInstance?.disconnect();
    };
  }, [setSocketInstance]);

  useEffect(() => {
    if (!socketInstance) return;

    const handler = async ({ goal, progress: actualProgress }) => {
      const { itemId, status, details, skillType, updatedStatusAt } = goal;

      setProgress(actualProgress);
      await checkReview(actualProgress);

      const updateSkills = (skills, setSkills) => {
        setSkills(
          skills.map((item) =>
            item.itemId === itemId ? { ...item, details, status, updatedStatusAt } : item,
          ),
        );
      };

      if (skillType === 'hard') updateSkills(hardSkills, setHardSkills);
      if (skillType === 'soft') updateSkills(softSkills, setSoftSkills);
      if (skillType === 'personal') updateSkills(personalSkills, setPersonalSkills);
    };

    socketInstance.on('receive-goal-status', handler);
    return () => socketInstance.off('receive-goal-status', handler);
  }, [hardSkills, softSkills, personalSkills, socketInstance, checkReview]);

  // Combine all skills for tree view
  const allSkills = useMemo(() => {
    return [...hardSkills, ...softSkills, ...personalSkills];
  }, [hardSkills, softSkills, personalSkills]);

  // Filter skills based on active filter
  const filteredSkills = useMemo(() => {
    if (activeFilter === 'all') return allSkills;
    if (activeFilter === 'hard') return hardSkills;
    if (activeFilter === 'soft') return softSkills;
    if (activeFilter === 'personal') return personalSkills;
    return allSkills;
  }, [allSkills, hardSkills, softSkills, personalSkills, activeFilter]);

  // Count skills in progress
  const skillsInProgressCount = useMemo(() => {
    return allSkills.filter((skill) => skill.status === GoalsConstants.GOAL_STATUSES.IN_PROGRESS)
      .length;
  }, [allSkills]);

  // Derive selectedSkill from allSkills to keep it in sync with real-time updates
  const selectedSkill = useMemo(() => {
    if (!selectedSkillId) return null;
    return allSkills.find((skill) => skill.itemId === selectedSkillId) || null;
  }, [selectedSkillId, allSkills]);

  const snackBarMessage = useMemo(() => {
    if (!isReviewRequested) {
      if (progress >= 100) {
        if (!isLastLevel) {
          if (!isEverythingInProgress) {
            return ReviewConstants.MESSAGES.DO_YOU_WANT_TO_SEND_A_REVIEW_REQUEST;
          }
          return ReviewConstants.MESSAGES.ALL_SKILLS_ARE_APPROVED;
        }
        return ReviewConstants.MESSAGES.YOU_GOT_ALL_LEVELS;
      }
    }

    if (isReviewRequested) {
      return ReviewConstants.MESSAGES.REVIEW_IS_REQUESTED_SUCCESSFULLY;
    }

    return null;
  }, [isEverythingInProgress, isLastLevel, isReviewRequested, progress]);

  const handleSkillSelect = useCallback((skill) => {
    setSelectedSkillId(skill?.itemId || null);
  }, []);

  const handleAddSkillToProgress = useCallback(
    (skill) => {
      if (!socketInstance || !skill) return;

      socketInstance.emit('send-goal-status', {
        itemId: skill.itemId,
        status: GoalsConstants.GOAL_STATUSES.IN_PROGRESS,
        skillType: skill.skillType,
      });
    },
    [socketInstance],
  );

  const handleDetailStatusChange = useCallback(
    ({ parentId, itemId, status }) => {
      if (socketInstance) {
        socketInstance.emit('send-nested-goal-status', {
          parentId,
          itemId,
          status,
        });
      }
    },
    [socketInstance],
  );

  if (isLoading && !_.isBoolean(isReviewRequested)) {
    return (
      <AppLayout title={title}>
        <Loading />
      </AppLayout>
    );
  }

  return (
    <AppLayout title={title}>
      <div className="flex h-full overflow-hidden">
        {/* Left Sidebar - Skill Tree (Resizable) */}
        <ResizablePanel className="h-full">
          <SkillTreeSidebar
            skills={filteredSkills}
            skillsInProgressCount={skillsInProgressCount}
            selectedSkill={selectedSkill}
            onSkillSelect={handleSkillSelect}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            isDisabled={isReviewRequested}
            reviewData={reviewData}
            snackBarMessage={snackBarMessage}
            onRequestReview={handleOnRequestReviewClick}
            isLoadingBtn={isLoadingBtn}
          />
        </ResizablePanel>

        {/* Right Content - Skill Detail View */}
        <div className="flex-1 min-w-0 pl-6 h-full">
          <SkillDetailView
            skill={selectedSkill}
            allSkills={allSkills}
            onLinkClick={handleLinkClick}
            onAddToProgress={handleAddSkillToProgress}
            onDetailStatusChange={handleDetailStatusChange}
            isDisabled={isReviewRequested}
          />
        </div>
      </div>

      {/* Congratulation Modal */}
      <Congratulation congratulate={congratulate} handleClose={() => setCongratulate(false)} />
    </AppLayout>
  );
}

HomeV3.propTypes = {
  title: PropTypes.string.isRequired,
};
